import { useEffect, useMemo, useState } from 'react';
import { easternIsoDay } from '../../data/taskRules';
import { Modal } from '../../ui/Modal';
import { useAppUser } from '../../auth/AppUserContext';
import { canConductAudits, canSeeStore } from '../../data/scope';
import { SubmissionKey } from '../../data/clientKey';
import { getAllStores } from '../../data/stores';
import {
  createTask,
  getCompletedTasks,
  getOpenTasks,
  getTasksForStore,
  type ServerTask,
} from '../../data/tasks';
import type { AppUser, Store } from '../../data/types';
import { ActionButton } from '../../ui/ActionButton';
import { AssigneeField } from '../../ui/AssigneeField';
import { StoreSearchSelect } from '../../ui/StoreSearchSelect';
import { TaskListView, type StatusFilter } from '../../ui/tasks/TaskListView';
import { useSearchParams } from 'react-router';
import { OutboxList } from '../../ui/tasks/OutboxList';
import { CompleteTaskModal } from '../../ui/tasks/CompleteTaskModal';
import { ReopenTaskModal } from '../../ui/tasks/ReopenTaskModal';
import { isoDaysAgo } from '../../ui/dates';

const STATUSES: readonly StatusFilter[] = ['open', 'completed', 'all'];

/**
 * Tasks hub (spec §6): role-scoped queues. Store teams see their store's
 * work and complete it (with the personal-accountability name field);
 * Auditors/DMs/Admins can also create ad-hoc tasks and reopen completed
 * ones; Executives read it (no actions). Comments and files queue offline
 * (B4, the outbox list here); completing needs signal.
 */
export function TasksPage() {
  // `?store=<id>&status=open|completed` — a store page's "View all" link (owner ask 2026-10-29).
  const [searchParams] = useSearchParams();
  const { user } = useAppUser();
  const [tasks, setTasks] = useState<ServerTask[] | null>(null);
  const [stores, setStores] = useState<Store[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [completing, setCompleting] = useState<ServerTask | null>(null);
  const [reopening, setReopening] = useState<ServerTask | null>(null);
  const [creating, setCreating] = useState(false);

  // A store page's "View all" (?store=<id>): that store's whole history too —
  // the recent-completions read below covers every store, so on its own it
  // could show 3 of a store's 37 completed tasks (QA 2026-10-29).
  const storeParam = Number(searchParams.get('store'));
  const storeAsked = Number.isInteger(storeParam) && storeParam > 0 ? storeParam : null;

  // Open + the most recent completed tasks together; the list's status
  // filter (open/completed/all) slices them client-side.
  const refresh = () => {
    setTasks(null);
    Promise.all([getOpenTasks(), getCompletedTasks(), storeAsked !== null ? getTasksForStore(storeAsked) : Promise.resolve([])])
      .then(([open, completed, ofStore]) => {
        const byId = new Map<string, ServerTask>();
        for (const t of [...open, ...completed, ...ofStore]) byId.set(String(t.id), t);
        setTasks([...byId.values()]);
      })
      .catch((err: unknown) =>
        setError(err instanceof Error ? err.message : String(err)),
      );
  };

  useEffect(() => {
    if (!user) return;
    refresh();
    // Include inactive stores so historical tasks can still resolve a name.
    getAllStores({ includeInactive: true })
      .then(setStores)
      .catch(() => setStores([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, storeAsked]);

  const storeById = useMemo(
    () => new Map(stores.map((s) => [Number(s.id), s])),
    [stores],
  );

  const filterableStores = useMemo(
    () =>
      user
        ? stores
            .filter((s) => s.active && canSeeStore(user, s))
            .sort((a, b) =>
              a.storeNumber.localeCompare(b.storeNumber, undefined, {
                numeric: true,
              }),
            )
        : [],
    [stores, user],
  );
  const scopedTasks = useMemo(() => {
    if (!user || tasks === null) return null;
    return tasks.filter((t) => {
      const store = storeById.get(t.storeId);
      // Inactive (divested) stores are hidden on every screen by default —
      // their tasks too; a task page link still opens them.
      if (store && !store.active) return false;
      // Personal assignment is always visible; otherwise store scope rules.
      if (t.assignedTo.toLowerCase() === user.upn.toLowerCase()) return true;
      return store ? canSeeStore(user, store) : false;
    });
  }, [tasks, user, storeById]);

  if (!user) return null;
  const canManage = canConductAudits(user.role);

  return (
    <div>
      <div className="page-head">
        <h1>Tasks</h1>
        <div className="page-head-tools">
          {canManage && (
            <button className="btn btn-primary" onClick={() => setCreating(true)}>
              New task
            </button>
          )}
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {/* Task comments/files waiting on this device (B4). */}
      <OutboxList />
      <TaskListView
        // A new link (or the top-bar Tasks link) starts the filters afresh.
        key={searchParams.toString()}
        pageSize={25}
        initialStoreId={searchParams.get('store') ?? ''}
        initialStatus={STATUSES.find((v) => v === searchParams.get('status')) ?? 'open'}
        tasks={scopedTasks}
        filterStores={filterableStores}
        storeById={storeById}
        canManage={canManage}
        // Executives are read-only (the server refuses their writes too).
        onComplete={user.role === 'Executive' ? undefined : setCompleting}
        onReopen={(t) => setReopening(t)}
      />

      {reopening && (
        <ReopenTaskModal
          task={reopening}
          onClose={() => setReopening(null)}
          onDone={() => {
            setReopening(null);
            refresh();
          }}
        />
      )}
      {completing && (
        <CompleteTaskModal
          task={completing}
          onClose={() => setCompleting(null)}
          onDone={() => {
            setCompleting(null);
            refresh();
          }}
        />
      )}
      {creating && (
        <NewTaskModal
          user={user}
          stores={filterableStores}
          onClose={() => setCreating(false)}
          onDone={() => {
            setCreating(false);
            refresh();
          }}
        />
      )}
    </div>
  );
}

function NewTaskModal({
  user,
  stores,
  onClose,
  onDone,
}: {
  user: AppUser;
  stores: Store[];
  onClose: () => void;
  onDone: () => void;
}) {
  const [storeId, setStoreId] = useState('');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [assignedTo, setAssignedTo] = useState('');
  const [dueDate, setDueDate] = useState(() => isoDaysAgo(-30));
  // Today (Eastern) is the earliest due date — the picker's min doesn't stop typing (final-build rule, 2026-10-26).
  const dueOk = /^\d{4}-\d{2}-\d{2}$/.test(dueDate) && dueDate >= easternIsoDay(new Date());
  // Off by default (owner ruling 2026-10-25, B1).
  const [proofRequired, setProofRequired] = useState(false);
  // Shown inside the dialog — the page's own error line sits behind it.
  const [saveError, setSaveError] = useState<string | null>(null);
  // Retrying the same task after a lost answer gets the task that landed, not a second one.
  const [taskKey] = useState(() => new SubmissionKey('task'));

  const selectedStore = stores.find((s) => s.id === storeId);
  const valid =
    storeId !== '' && title.trim() !== '' && assignedTo.trim() !== '' && dueOk;

  return (
    <Modal onClose={onClose} dirty={title.trim() !== '' || description.trim() !== ''}>
        <h2>New task</h2>
        <div className="form-grid">
          <label>
            Store
            <StoreSearchSelect
              stores={stores}
              value={storeId}
              onChange={(id) => {
                setStoreId(id);
                const store = stores.find((s) => s.id === id);
                if (store && !assignedTo) setAssignedTo(store.managerEmail);
              }}
              required
              placeholder="Type to search stores…"
            />
          </label>
          <label>
            Due date
            <input
              type="date"
              min={easternIsoDay(new Date())}
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
              aria-invalid={dueDate !== '' && !dueOk ? true : undefined}
            />
            {dueDate !== '' && !dueOk && <span className="error" style={{ fontSize: '0.85rem' }}>Pick today or a later day.</span>}
          </label>
          <label className="span-2">
            Title
            <input required value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="span-2">
            Description
            <textarea
              className="q-input"
              rows={3}
              maxLength={10_000}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </label>
          <AssigneeField
            label="Assign to"
            value={assignedTo}
            onChange={setAssignedTo}
            required
          />
          <label className="checkbox span-2">
            <input type="checkbox" checked={proofRequired} onChange={(e) => setProofRequired(e.target.checked)} />
            Proof required (photo or PDF to complete)
          </label>
        </div>
        {selectedStore && assignedTo === selectedStore.managerEmail && (
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            Assigned to the store’s shared account — anyone at the store can
            work it.
          </p>
        )}
        {saveError && (
          <p className="error" role="alert">
            {saveError}
          </p>
        )}
        <div className="modal-actions">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <ActionButton
            className="btn btn-primary"
            disabled={!valid}
            busyLabel="Creating…"
            onAction={async () => {
              setSaveError(null);
              try {
                const task = {
                  title: title.trim(),
                  description: description.trim(),
                  storeId: Number(storeId),
                  assignedTo: assignedTo.trim(),
                  dueDate,
                  createdBy: user.upn,
                  createdByName: user.displayName,
                  ...(proofRequired ? { proofRequired: true } : {}),
                };
                // One key while this dialog is open: a retry after a lost answer — even with edits — is
                // the SAME task (a second one would also mean a second "assigned" email; QA 2026-10-21).
                await createTask({ ...task, clientKey: taskKey.for('this dialog') });
                taskKey.reset();
              } catch (err) {
                setSaveError(err instanceof Error ? err.message : String(err));
                return;
              }
              onDone();
            }}
          >
            Create task
          </ActionButton>
        </div>
    </Modal>
  );
}
